import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Input, Modal, Segmented, Select, message } from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';

// New Evaluation without a call, for a client whose CCaaS system we do not use
// (routes/cases.py _create_no_call_case). The server sends what the user may
// pick: forms ticked "Allow without a call" with their campaigns cut to the
// user's Visible Campaigns, and agents on the user's Visible Teams. Campaign
// comes first so the Form list is only forms on that campaign. The agent, date
// and External Ref are entered here because there is no call to take them from.
export default function NoCallEvalModal({ open, forms, agents, currentUser, onClose, onCreated }) {
  const [form]       = Form.useForm();
  const lockConfig   = useLockConfig();
  const [saving, setSaving]       = useState(false);
  const [assignees, setAssignees] = useState([]);
  const campaignId = Form.useWatch('campaign_id', form);

  const campaigns = useMemo(() => {
    const byId = new Map();
    forms.forEach((f) => f.Campaigns.forEach((c) => byId.set(c.ID, c)));
    return [...byId.values()].sort((a, b) => a.Name.localeCompare(b.Name));
  }, [forms]);
  const campaignForms = forms.filter((f) => f.Campaigns.some((c) => c.ID === campaignId));

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    const only = campaigns.length === 1 ? campaigns[0].ID : null;
    const onlyForms = forms.filter((f) => f.Campaigns.some((c) => c.ID === only));
    form.setFieldsValue({
      eval_type: 'evaluation',
      campaign_id: only,
      form_id: onlyForms.length === 1 ? onlyForms[0].ID : null,
      call_date: dayjs(),
    });
  }, [open]); // eslint-disable-line

  // Assign to: people with Evaluations access on the chosen campaign.
  useEffect(() => {
    setAssignees([]);
    if (!campaignId) return;
    api.get('/api/users', { params: { can_edit_evals: 1, campaign_id: campaignId } })
      .then((r) => {
        setAssignees(r.data);
        if (!form.getFieldValue('assigned_to_user_id') && r.data.some((u) => u.ID === currentUser?.ID)) {
          form.setFieldValue('assigned_to_user_id', currentUser.ID);
        }
      })
      .catch(() => {});
  }, [campaignId]); // eslint-disable-line

  const onCampaignChange = (id) => {
    const list = forms.filter((f) => f.Campaigns.some((c) => c.ID === id));
    form.setFieldsValue({ form_id: list.length === 1 ? list[0].ID : null, assigned_to_user_id: null });
  };

  const handleCreate = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    const date = v.call_date.format('YYYY-MM-DD');
    if (checkLocked(lockConfig, 'eval', date)) {
      message.error('This date is before the configured lock date. Evaluations cannot be created for locked dates.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/api/cases', {
        form_id:             v.form_id,
        campaign_id:         v.campaign_id,
        agent_user_id:       v.agent_user_id,
        assigned_to_user_id: v.assigned_to_user_id,
        call_date:           date,
        external_ref:        v.external_ref.trim(),
        is_feedback_only:    v.eval_type === 'feedback_only' ? 1 : 0,
      });
      message.success('Evaluation created.');
      onClose();
      onCreated?.(res.data.id);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create evaluation.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New Evaluation" open={open} onOk={handleCreate} onCancel={onClose}
      confirmLoading={saving} okText="Create" maskClosable={false} destroyOnClose width={520}>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Type" name="eval_type" required>
          <Segmented options={[
            { label: 'Evaluation',    value: 'evaluation' },
            { label: 'Feedback Only', value: 'feedback_only' },
          ]} />
        </Form.Item>
        <Form.Item label="Campaign" name="campaign_id" rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select a campaign…" onChange={onCampaignChange}
            options={campaigns.map((c) => ({ label: c.Name, value: c.ID }))} />
        </Form.Item>
        <Form.Item label="Evaluation Form" name="form_id" rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select a form…" disabled={!campaignId}
            options={campaignForms.map((f) => ({ label: `${f.Name} (v${f.Version})`, value: f.ID }))} />
        </Form.Item>
        <Form.Item label="Agent" name="agent_user_id" rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select an agent…"
            options={agents.map((u) => ({   // as Create Error lists them: Name (Team)
              label: u.TeamName ? `${u.DisplayName || u.Email} (${u.TeamName})` : (u.DisplayName || u.Email),
              value: u.ID,
            }))} />
        </Form.Item>
        <Form.Item label="Assign to" name="assigned_to_user_id" rules={[{ required: true, message: 'Required' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select a user…" disabled={!campaignId}
            options={assignees.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))} />
        </Form.Item>
        <Form.Item label="Date" name="call_date" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD"
            disabledDate={(d) => d && (d.isAfter(dayjs(), 'day') || checkLocked(lockConfig, 'eval', d.format('YYYY-MM-DD')))} />
        </Form.Item>
        <Form.Item label="External Ref" name="external_ref"
          rules={[{ required: true, whitespace: true, message: 'Required' }]}>
          <Input allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );
}
